// vitest からは Nuxt auto-import が効かないため明示 import に。Nuxt 側は明示 import が
// あっても auto-import を上書きする形で問題なく動く (= 振る舞い変わらず、テスト可能になる)。
import { ref, readonly, computed } from 'vue'
import { storage } from '~/utils/session-storage'
import { useAuth } from './auth'
import { requestOrThrow } from './api-client'
import { Form as EditForm } from '~/components/me/edit/edit-form.vue'
import { buildMeUpdateBody } from './me/build-update-body'
import type { GetMe200 } from '~/types/api/Me'
import type { CamelCaseKeys } from '~/types/util'

const storageKey = 'me'

// Sample migration to generated types (Notion 計画書 段階 2.5).
// GetMe200 is the kubb-generated response shape for GET /me;
// CamelCaseKeys mirrors the snake → camel conversion that
// custom-fetch.ts performs at runtime.
type MeApiResponse = CamelCaseKeys<GetMe200>

export type Me = MeApiResponse

const { getItem, setItem, removeItem } = storage<Me>(storageKey)
const me = ref(getItem())

// プロフィール項目(氏名/住所/連絡先等)は proxyGroupMember.profile 配下に格納される。
// 未取得(空 me)時は空オブジェクトを返し、各項目を undefined として安全に参照できるようにする。
export type MeProfile = MeApiResponse['proxyGroupMember']['profile']
const profile = computed(() => me.value.proxyGroupMember?.profile ?? ({} as MeProfile))

const get = async (forceLatest?: boolean): Promise<Me> => {
  // proxyGroupMember.profile を持つ新構造の me のみキャッシュ採用。空 me({})や旧セッションの
  // フラットな me は profile 参照が全面的に壊れるため、ここで弾いて API から取り直す。
  if (!forceLatest && me.value.proxyGroupMember?.profile) return readonly(me.value)

  const { auth } = useAuth()

  const data = await requestOrThrow<MeApiResponse>(`me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${auth.value.sessionId}`,
    },
  })

  me.value = data
  setItem(me.value ?? {})
  return me.value
}

const update = async (form: EditForm) => {
  const { auth } = useAuth()
  const body = buildMeUpdateBody(form)

  const data = await requestOrThrow<MeApiResponse>(`me`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${auth.value.sessionId}`,
    },
    body,
  })
  me.value = data
  setItem(me.value ?? {})
}

const updatePassword = async (password: string) => {
  await requestOrThrow(`me`, {
    method: 'PATCH',
    body: {
      password,
    },
  })
}

const updateAddressesRuby = async (
  municipalitiesRuby: string,
  streetRuby: string,
  addressRuby: string,
  buildingRuby: string
) => {
  const { auth } = useAuth()
  const body = {
    municipalitiesRuby,
    streetRuby,
    addressRuby,
    buildingRuby,
  }
  const data = await requestOrThrow<MeApiResponse>(`me`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${auth.value.sessionId}`,
    },
    body,
  })
  me.value = data
  setItem(me.value ?? {})
}

/**
 * me キャッシュを破棄する(サインイン時に useAuth().get() から呼ばれる)。
 * サインアウトは clearSessionStorage が全 key を消すが、セッション失効後の再サインインや
 * 別アカウントでのサインインはそこを通らないため、前ユーザーの me(本人 proxyGroupMemberId 等)を
 * 引き継いだままにすると、代理対象者(getTargets)の本人が前ユーザーの pgm になり、退会済み等の
 * 他人 pgm を target として送って 403 になる。resetGroupState と同方針でここを担保する。
 */
export const resetMeState = () => {
  removeItem()
  me.value = getItem()
}

export const useMe = () => {
  return {
    me,
    profile,
    get,
    update,
    updatePassword,
    updateAddressesRuby,
  }
}
